import { ProductsContext } from "Components/GMS/Products/ProductsContext";
import { useISRContext } from "Pages/GMS/ISR/Hooks/useISRContext";
import { ABContext } from "Pages/GMS/ABTesting/contexts/ABContext";
import React, { startTransition, useContext, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { toast } from 'Components/DesignSystem/Toast/useToast';
import { AppContext } from 'Context/AppContext';
import UnsavedChangesModal from 'Components/Common/UnsavedChangesModal';
import Tooltip from 'Components/DesignSystem/Tooltip/Tooltip';
import { FOCUS_RING_INSET } from 'Components/DesignSystem/focusRing';

type MenuProps = {
  id: string;
  path: string;
  iconSrc: string;
  filledIconSrc: string;
  label: string;
  matchPath?: string;
  show: boolean;
  menu: object;
}

// matchPath/path values are path-to-regexp-style patterns (some, like
// "/gms/product(s)?", rely on regex groups) matched non-exactly against the
// current location, same as v5's useRouteMatch({path}) without `exact`.
const isPathActive = (pathname: string, pattern: string): boolean => {
  return new RegExp(`^${pattern}(?:/.*)?$`).test(pathname);
};

const MenuItem = ({ id, path, iconSrc, filledIconSrc, label, show, menu, matchPath, }: MenuProps) => {
  const isrContext = useISRContext();
  const abContext = useContext(ABContext);
  const appContext = useContext(AppContext);
  const productContext = useContext<any>(ProductsContext);
  const navigate = useNavigate();
  const location = useLocation();
  const match = isPathActive(location.pathname, matchPath ? matchPath : path);
  const isWorkspaceNotReady = location.pathname === "/workspace/preparing" ||
    location.pathname === "/workspace/notfound" ||
    location.pathname === "/workspace/industry/notfound";
  const createPermission = appContext.pm.checkPermission('gmsadministration_create');
  const updatePermission = appContext.pm.checkPermission('gmsadministration_update');

  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingPath, setPendingPath] = useState<string | null>(null);
  const [saveInProgress, setSaveInProgress] = useState(false);

  if (label === 'Manage Parties' && (!updatePermission ||!createPermission)) {
    return null;
  }

  // Admin › Adapters is a Compatio-Admin-only screen (AB#14369): without the `GMSAdapter`
  // module's read permission the item must not exist in the nav at all, not merely dim —
  // a Party Admin should have no hint the module is there. The page redirects on direct
  // URL access as well (`Components/Admin/adapters/AdaptersView.tsx`).
  if (label === 'Adapters' && !appContext.pm.checkPermission('gmsadapter_read')) {
    return null;
  }

  const catalogueReadDisabled = label === 'Review' && !appContext.pm.checkPermission('gmscatalogue_read');
  const mappingReadDisabled = label === 'Mapping' && !appContext.pm.checkPermission('gmsdatamapping_read');
  const connectorReadDisabled = label === 'Connectors' && !appContext.pm.checkPermission('gmsconnector_read');

  const hasHandlers = appContext?.editSaveHandler && appContext?.editCancelHandler;

  // react-router v7's <Link>/navigate() schedule the route change as a transition (lower
  // priority, interruptible). A plain setState here runs at normal priority and jumps the queue
  // ahead of that pending transition, forcing one extra render of the page being left — with its
  // now-stale route state — before the transition resolves. That extra render is enough to refire
  // ISRHomePage's category-fetch effect, keyed on this same (but freshly-identical) pagination
  // object, producing a wasted request. Wrapping in startTransition puts this reset at the same
  // priority as the pending navigation, so both resolve together and the page being left has
  // already unmounted by the time this applies — it does not just delay the update.
  const resetOtherModulesPagination = () => {
    startTransition(() => {
      productContext.setCategoryData([]);
      isrContext?.setPaginationParams({pageNumber: 1, pageSize: 10});
      abContext?.setPaginationParams({pageNumber: 1, pageSize: 10});
    });
  };

  const performNavigation = (targetPath: string) => {
    navigate(targetPath);
    resetOtherModulesPagination();
  };

  const handleMenuClickEvent = (e: React.MouseEvent): void => {
    if (appContext?.editInProgress) {
      e.preventDefault();
      if (hasHandlers) {
        setPendingPath(path);
        setShowUnsavedModal(true);
      } else {
        toast.warning('Please save or cancel your edits before navigating');
      }
      return;
    }
    resetOtherModulesPagination();
  }

  const handleModalSave = async () => {
    if (!appContext?.editSaveHandler) return;
    setSaveInProgress(true);
    try {
      const success = await appContext.editSaveHandler();
      if (success && pendingPath) {
        setShowUnsavedModal(false);
        performNavigation(pendingPath);
        setPendingPath(null);
      }
    } finally {
      setSaveInProgress(false);
    }
  };

  const handleModalDiscard = () => {
    appContext?.editCancelHandler?.();
    setShowUnsavedModal(false);
    if (pendingPath) {
      performNavigation(pendingPath);
      setPendingPath(null);
    }
  };

  const handleModalCancel = () => {
    setShowUnsavedModal(false);
    setPendingPath(null);
  };

  const isDisabled =
    isWorkspaceNotReady || catalogueReadDisabled || mappingReadDisabled || connectorReadDisabled;

  /*
    The padding, the rounding and the state ground all live on the ROW ELEMENT — the
    <Link>, or the inert <div> that replaces it when the item is gated. They used to
    live on the <li>, with the <Link> a shrink-wrapped child inside it: measured, the
    highlighted row was 224x32 while the clickable <a> inside it was 210x18. So 44%
    of the row's height lit up on hover and did nothing on click, and 18px fails WCAG
    2.5.8's 24x24 target minimum outright. `w-full` + the padding here make the target
    the row. See `okf/templates/e2e.md` § 18.

    FOCUS_RING_INSET, not FOCUS_RING: the sidebar is `overflow-y-auto`, and a ring is
    a box-shadow, which a clipping ancestor cuts off. Only a negative outline-offset
    survives — see `focusRing.ts`.
  */
  const rowClass = `tw-block tw-w-full tw-py-[7px] tw-px-3 tw-rounded-md tw-transition-colors`;

  /*
    18px box on every icon. The assets carry FIVE different intrinsic sizes (16x18,
    18x12, 18x18, 19x18, 19x19) and had no size class, inside a flex row with the
    default `align-items: stretch` — so Performance's 18x12 gauge was drawn at 18x18,
    stretched 1.5x, and the label's left edge walked between 28px and 31px down the
    list. `object-contain` letterboxes rather than distorts; `items-center` on the row
    stops the stretch at source.

    `alt` is empty while the label is visible — the icon is decorative there and the
    text names the link. In the collapsed rail there IS no text, so the alt becomes
    the name. It used to be the literal string "close" on all 18 icons in both states.

    Dropped with the size class: `hover:tw-backdrop-hue-rotate-30`, which is a real
    generated utility (it is in the built CSS) that hue-rotated the row's background
    behind the 18px icon box on hover — a tinted square, not an effect anyone asked
    for.
  */
  /*
    Preserved exactly as it was: the gated branch has ALWAYS drawn the plain glyph,
    never the filled one, even on a route its own `matchPath` matches. Folding the two
    branches onto one `match ? filled : plain` would have been a silent behaviour
    change for a permission-gated item sitting on its own route.
  */
  const rowIconSrc = isDisabled ? iconSrc : (match ? filledIconSrc : iconSrc);

  /*
    A STENCIL, not an <img> (2026-09-02). The icon files carry baked-in colours —
    mostly greys plus several navy fills (Guide's filled state, Lists, A/B, PDW) —
    and CSS cannot re-ink pixels inside an <img>, so those glyphs stayed navy on
    the dark theme. `.nav-icon-mask` (index.css) uses the SAME file as a mask and
    paints it with currentColor, so every icon follows the row's themed ink:
    `ink-secondary` at rest, `accent` when active — in both themes, all 36 files,
    without editing any of them. Multi-tone art flattens to one colour, which is
    the design system's nav-icon grammar anyway.

    The accessibility split mirrors the old alt="" / alt={label}: decorative while
    the label is visible, the row's name in the collapsed rail.

    The url() MUST be quoted: Vite inlines the small icons as URL-encoded data URIs
    that contain SINGLE QUOTES (`width='18'`), which are illegal in an unquoted CSS
    url() — the browser rejected the whole declaration and the icon rendered as a
    plain currentColor square. Only substitution-icon.svg (too big to inline, so a
    quote-free path URL) survived unquoted, which is what gave the bug away.
  */
  const iconCssUrl = `url("${rowIconSrc.replace(/"/g, '%22')}")`;

  const iconAndLabel = (
    <div id={`${id}-content`} className={`tw-flex tw-items-center tw-gap-2`}>
      <span
        id={`${id}-icon`}
        role={show ? undefined : "img"}
        aria-label={show ? undefined : label}
        aria-hidden={show ? true : undefined}
        className={`tw-h-[18px] tw-w-[18px] tw-shrink-0 nav-icon-mask`}
        style={{ "--icon-url": iconCssUrl } as React.CSSProperties}
      />
      {show && (
        <span
          id={`${id}-label`}
          /*
            Both states are 12px so navigating never reflows the row; the ACTIVE one
            steps 500 -> 600. Weight, not colour alone (DS § 60) — needed here because
            `accent-tint` and `surface-sunken` sit at 1.10 and 1.09 against white, so
            the two grounds alone barely separate. Colour is inherited from the row.
          */
          className={`tw-truncate ${match ? "tw-text-sub-title-label" : "tw-text-sub-body-supporting"}`}
        >
          {label}
        </span>
      )}
    </div>
  );

  const row = isDisabled ? (
    <div
      id={`${id}-link`}
      className={`${rowClass} tw-text-ink-secondary tw-pointer-events-none`}
    >
      {iconAndLabel}
    </div>
  ) : (
    <Link
      id={`${id}-link`}
      data-testid={`${id}-link`}
      to={path ?? "#"}
      onClick={handleMenuClickEvent}
      key={label}
      /*
        Selected and hover used to be the SAME colour (#dee3f6), so hovering the
        selected row was a no-op and a hovered row was indistinguishable from the
        selected one. Split onto the DS selection grammar (§ 51/§ 65): selected is
        `accent` on `accent-tint`, the same pair Dropdown / Menu / Tab / ChoiceChip
        already use; hover is the neutral `surface-sunken` row ground.
      */
      aria-current={match ? "page" : undefined}
      className={`${rowClass} ${FOCUS_RING_INSET} ${
        match
          ? "tw-bg-accent-tint tw-text-accent"
          : "tw-text-ink-secondary hover:tw-bg-surface-hover-strong"
      }`}
    >
      {iconAndLabel}
    </Link>
  );

  return (
    <>
      <li
        id={id}
        data-testid={id}
        className={`tw-my-0.5 tw-mx-2 ${
          isDisabled ? "tw-opacity-50 tw-cursor-not-allowed" : ""
        } ${appContext?.editInProgress && !hasHandlers ? "tw-opacity-50 tw-cursor-not-allowed" : ""}`}
      >
        {/*
          The collapsed rail is icons with no text. Without this it is 18 links whose
          only accessible name is an alt attribute, and nothing at all for a sighted
          user who cannot remember which glyph is which. `block` so the tooltip's
          wrapper spans do not shrink-wrap the full-width row inside them; `usePortal`
          because the sidebar clips (`overflow-y-auto`) and an anchored bubble would
          be cut off at its right edge.
        */}
        {show ? row : (
          <Tooltip content={label} placement="right" usePortal block>
            {row}
          </Tooltip>
        )}
      </li>
      {showUnsavedModal && (
        <UnsavedChangesModal
          id="menu-item-unsaved-changes-modal"
          title="Unsaved Changes"
          message="You have unsaved changes. Do you want to save them before navigating?"
          confirmText="Save Changes"
          cancelText="Cancel"
          discardText="Discard changes"
          onClose={handleModalCancel}
          onConfirm={handleModalSave}
          onDiscard={handleModalDiscard}
          saveInProgress={saveInProgress}
        />
      )}
    </>
  );
};

export default MenuItem;
